import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NoteList from '../components/NoteList';
import { CategoryFilter } from '../components/CategoryFilter';
import { TagFilter } from '../components/TagFilter';
import { useCategories } from '../hooks/useCategories';
import { useTags } from '../hooks/useTags';
import { apiClient } from '../services/api';
import type { Note } from '../types/note';

export default function NotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { categories, loading: categoriesLoading } = useCategories('note');
  const { tags, loading: tagsLoading } = useTags('note');

  useEffect(() => {
    loadNotes();
  }, [selectedCategory, selectedTags]);

  const loadNotes = async () => {
    setIsLoading(true);
    const params: any = { category: 'note' };

    if (selectedCategory) {
      params.categoryId = selectedCategory;
    }

    if (selectedTags.length > 0) {
      params.tags = selectedTags.join(',');
    }

    const result = await apiClient.getNotes(params);
    if (result.data) {
      setNotes(result.data.notes);
    }
    setIsLoading(false);
  };

  const handleNoteClick = (note: Note) => {
    navigate(`/notes/${note.id}`);
  };

  const handleDeleteNote = async (id: string) => {
    if (confirm('确定要删除这条笔记吗？')) {
      const result = await apiClient.deleteNote(id);
      if (result.data) {
        setNotes(notes.filter((n) => n.id !== id));
      }
    }
  };

  const handleTagSelect = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const filteredNotes = notes.filter(note => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        note.title.toLowerCase().includes(query) ||
        note.content.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="notes-page">
      <div className="notes-header">
        <h2>📝 笔记</h2>
        <button
          onClick={() => navigate('/notes/new')}
          className="create-note-btn"
        >
          + 新建笔记
        </button>
      </div>

      <div className="notes-layout">
        <aside className="notes-sidebar">
          <div className="search-box">
            <input
              type="text"
              placeholder="搜索笔记..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            loading={categoriesLoading}
          />

          <TagFilter
            tags={tags}
            selectedTags={selectedTags}
            onSelectTag={handleTagSelect}
            loading={tagsLoading}
          />
        </aside>

        <main className="notes-content">
          {isLoading ? (
            <div className="notes-loading">加载中...</div>
          ) : filteredNotes.length === 0 ? (
            <div className="notes-empty">
              <p>暂无笔记</p>
              <button onClick={() => navigate('/notes/new')}>
                创建第一条笔记
              </button>
            </div>
          ) : (
            <NoteList
              notes={filteredNotes}
              onNoteClick={handleNoteClick}
              onDeleteNote={handleDeleteNote}
            />
          )}
        </main>
      </div>

      <style>{`
        .notes-page {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .notes-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .notes-header h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: #333;
        }

        .create-note-btn {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .create-note-btn:hover {
          background: #0056b3;
        }

        .notes-layout {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 24px;
        }

        .notes-sidebar {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .search-box {
          background: white;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .search-box input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
        }

        .search-box input:focus {
          border-color: #007bff;
        }

        .notes-content {
          background: white;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          min-height: 400px;
        }

        .notes-loading,
        .notes-empty {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .notes-empty p {
          font-size: 16px;
          margin-bottom: 16px;
        }

        .notes-empty button {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .notes-empty button:hover {
          background: #0056b3;
        }

        @media (max-width: 768px) {
          .notes-page {
            padding: 16px;
          }

          .notes-layout {
            grid-template-columns: 1fr;
          }

          .notes-sidebar {
            order: 2;
          }

          .notes-content {
            order: 1;
          }
        }
      `}</style>
    </div>
  );
}
